import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, ClipboardList, CalendarDays,
  FileText, LogOut, Menu, X, Truck, User
} from 'lucide-react';

const DriverLayout = () => {
  const [driver, setDriver] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('driver_token');
    const user = localStorage.getItem('driver_user');
    if (!token || !user) {
      navigate('/go');
      return;
    }
    try { setDriver(JSON.parse(user)); } catch { navigate('/go'); }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('driver_token');
    localStorage.removeItem('driver_user');
    navigate('/go');
  };

  const navItems = [
    { path: '/go/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/go/jobs', icon: ClipboardList, label: 'My Jobs' },
    { path: '/go/availability', icon: CalendarDays, label: 'Availability' },
    { path: '/go/documents', icon: FileText, label: 'Documents' },
  ];

  const isActive = (path) => location.pathname === path;

  if (!driver) return null;

  return (
    <div className="min-h-screen bg-[#0F172A]" data-testid="driver-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-[#1E293B] border-r border-[#334155] z-50 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-5 border-b border-[#334155]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-extrabold text-white tracking-tight">
                TRANSA <span className="text-[#10B981]">GO</span>
              </h1>
            </div>
          </div>
          <button className="absolute top-5 right-4 lg:hidden text-[#94A3B8]" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1 flex-1">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                isActive(item.path)
                  ? 'bg-[#10B981]/10 text-[#10B981]'
                  : 'text-[#94A3B8] hover:text-white hover:bg-[#334155]/50'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#334155]">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#94A3B8] hover:text-red-400 hover:bg-red-500/5 w-full transition-all"
            data-testid="driver-logout"
          >
            <LogOut className="w-5 h-5" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="h-16 bg-[#1E293B] border-b border-[#334155] flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <button className="lg:hidden text-[#94A3B8]" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3 ml-auto">
            <div className="text-right">
              <p className="text-sm font-medium text-white">{driver.name}</p>
              <p className="text-xs text-[#64748B]">{driver.vehicle_type || 'Driver'}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-[#10B981]/10 flex items-center justify-center">
              <User className="w-4 h-4 text-[#10B981]" />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DriverLayout;
